import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Helmet } from 'react-helmet-async';
import './Contact.css';
import fpPromise from '@fingerprintjs/fingerprintjs';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    type: 'feedback', // Default to feedback
    message: '',
    website_url: '' // Honeypot
  });
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [error, setError] = useState(null);
  const [deviceId, setDeviceId] = useState('');
  const recaptchaRef = useRef(null);

  const [fpLoaded, setFpLoaded] = useState(false);

  const initFingerprint = () => {
    if (fpLoaded) return;
    setFpLoaded(true);
    // Defer FingerprintJS execution (INP optimization)
    const init = async () => {
      try {
        const fp = await fpPromise.load();
        const result = await fp.get();
        setDeviceId(result.visitorId);
      } catch {
        setDeviceId('fallback-' + Date.now());
      }
    };
    
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(init);
    } else {
      setTimeout(init, 500);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaVerified) {
      setError('Please complete the CAPTCHA verification.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          type: formData.type,
          captchaToken: formData.captchaToken,
          website_url: formData.website_url,
          deviceId // Advanced Bot Mitigation
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setIsSubmitted(true);
        if (data.ticketId) setTicketId(data.ticketId);
        setFormData({ name: '', email: '', subject: '', message: '', type: 'feedback' });
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setCaptchaVerified(false);
      }
    } catch {
      setError('Could not reach the server. Please try again later.');
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaVerified(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact Us | Dynamic Exams</title>
        <meta name="description" content="Get in touch with Dynamic Exams. Contact our support team for queries regarding Olympiads, registrations, and school partnerships in Mathura." />
        <meta property="og:title" content="Contact Us | Dynamic Exams" />
        <meta property="og:description" content="Reach out for any queries regarding Olympiads, Registrations, or Partnerships." />
      </Helmet>
      <section className="page-header contact-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p className="page-subtitle">We are here to help! Reach out for any queries regarding Olympiads, Registrations, or Partnerships.</p>
        </div>
      </section>

      <section className="contact-content section-padding">
        <div className="container">
          <div className="contact-grid">
            
            {/* Contact Information */}
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <p className="info-desc">Feel free to contact us through any of the following channels. Our support team is active and ready to assist you.</p>
              
              <div className="info-cards">
                <div className="info-card glass-panel">
                  <div className="info-card-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4>Our Office</h4>
                    <p>212/1, Shanti Niketan,<br/>Dampier Nagar, Mathura,<br/>Uttar Pradesh - 281001</p>
                  </div>
                </div>



                <div className="info-card glass-panel">
                  <div className="info-card-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4>Email</h4>
                    <p><a href="mailto:mathuraolympiads@gmail.com" style={{ color: 'inherit', textDecoration: 'none' }}>mathuraolympiads@gmail.com</a></p>
                  </div>
                </div>

                <div className="info-card glass-panel">
                  <div className="info-card-icon">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h4>Office Hours</h4>
                    <p>Mon – Sat: 9:00 AM – 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper glass-panel">
              <h2>Send a Message</h2>
              <p className="form-desc">Have a question or feedback? Fill out the form below and we will get back to you within 24 hours.</p>
              
              {isSubmitted ? (
                <div className="success-message" style={{textAlign: 'center', padding: '2rem 0'}}>
                  <div style={{fontSize: '3rem', marginBottom: '1rem'}}>✅</div>
                  <h3 style={{color: 'var(--success)', marginBottom: '1rem'}}>Successfully Submitted!</h3>
                  {ticketId ? (
                    <>
                      <p style={{color: 'var(--text-secondary)'}}>Your concern has been logged securely.</p>
                      <div style={{background: 'rgba(59,130,246,0.1)', border: '1px solid var(--primary)', padding: '1rem', borderRadius: '8px', margin: '1rem 0'}}>
                        <span style={{fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.5rem'}}>Your Ticket ID</span>
                        <strong style={{fontSize: '1.5rem', color: 'var(--primary)', fontFamily: 'monospace'}}>{ticketId}</strong>
                      </div>
                      <p style={{color: 'var(--text-secondary)', fontSize: '0.85rem'}}>Please save this Ticket ID for future reference. We will contact you at your email address.</p>
                    </>
                  ) : (
                    <p style={{color: 'var(--text-secondary)'}}>Thank you for your feedback! Our team will review it shortly.</p>
                  )}
                  <button className="btn btn-outline mt-4" onClick={() => { setIsSubmitted(false); setTicketId(null); setCaptchaVerified(false); }}>Send Another Message</button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} onFocus={initFingerprint} onMouseEnter={initFingerprint}>
                  {error && (
                    <div style={{color: 'var(--error)', padding: '0.75rem 1rem', backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem'}}>
                      {error}
                    </div>
                  )}
                  {/* HONEYPOT FIELD (INVISIBLE) */}
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <label htmlFor="website_url">Website (Leave Blank)</label>
                    <input type="text" id="website_url" name="website_url" value={formData.website_url} onChange={handleChange} tabIndex="-1" autoComplete="off" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input type="text" id="name" required placeholder="Enter your full name" value={formData.name} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input type="email" id="email" required placeholder="Enter your email" value={formData.email} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="type">How can we help? *</label>
                      <select id="type" value={formData.type} onChange={handleChange} required className="form-input">
                        <option value="feedback">Share Feedback / General Query</option>
                        <option value="concern">Raise a Concern / Issue</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject *</label>
                      <input type="text" id="subject" required placeholder="What is this regarding?" value={formData.subject} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message">Your Message *</label>
                    <textarea id="message" rows="5" required placeholder="Write your message here..." value={formData.message} onChange={handleChange}></textarea>
                  </div>
                  
                  <div style={{ minHeight: '78px', marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                    <ReCAPTCHA 
                      ref={recaptchaRef}
                      sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LfkRKstAAAAAKaXSZjjig5Ad3e-riGiqR04GWFk'}
                      onChange={(token) => {
                          setCaptchaVerified(true);
                          setFormData({ ...formData, captchaToken: token });
                      }} 
                      onExpired={() => {
                          setCaptchaVerified(false);
                          setFormData({ ...formData, captchaToken: '' });
                      }}
                    />
                  </div>

                  <button type="submit" className="btn btn-primary submit-btn" disabled={isSubmitting || !captchaVerified} style={{width: '100%', padding: '1rem', fontSize: '1rem'}}>
                    {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={18} style={{marginLeft: '0.5rem'}} />
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
